import React, { useState, useEffect } from "react";
import { Container, Box, Pagination } from "@mui/material";
import { useTranslation } from "react-i18next";
import { sellerReviewService, SellerReview } from "../../services/sellerReviews";
import ReviewsHeader from "./seller-reviews/components/ReviewsHeader";
import ReviewsErrorState from "./seller-reviews/components/ReviewsErrorState";
import ReviewsLoadingState from "./seller-reviews/components/ReviewsLoadingState";
import ReviewsEmptyState from "./seller-reviews/components/ReviewsEmptyState";
import ReviewsGrid from "./seller-reviews/components/ReviewsGrid";
import ReviewEditDialog from "./seller-reviews/components/ReviewEditDialog";
import ReviewCreateDialog from "./seller-reviews/components/ReviewCreateDialog";

const SellerReviews: React.FC = () => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<SellerReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 3;
  const [editingReview, setEditingReview] = useState<SellerReview | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const fetchReviews = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, count, error: fetchError } = await sellerReviewService.getAllReviews(page, limit);
      if (fetchError) throw fetchError;
      setReviews(data || []);
      setTotalPages(Math.ceil((count || 0) / limit) || 1);
    } catch (err: any) {
      console.error("Error fetching reviews:", err);
      setError(err.message || t("admin.sellerReviews.fetchError", { defaultValue: "Failed to load reviews" }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [page]);

  const handleDelete = async (id: string) => {
    if (window.confirm(t("admin.sellerReviews.deleteConfirm", { defaultValue: "Are you sure you want to delete this review?" }))) {
      try {
        const { error: deleteError } = await sellerReviewService.deleteReview(id);
        if (deleteError) throw deleteError;
        setReviews(reviews.filter((r) => r.id !== id));
      } catch (err: any) {
        console.error("Error deleting review:", err);
        alert(t("common.errorDelete", { defaultValue: "Failed to delete review" }));
      }
    }
  };

  const handleEdit = async (reviewId: string, updates: any) => {
    try {
      const { data, error: updateError } = await sellerReviewService.updateReview(reviewId, updates);
      if (updateError) throw updateError;
      setReviews(reviews.map((r) => (r.id === reviewId ? { ...r, ...data, seller: r.seller, reviewer: r.reviewer } : r)));
    } catch (err: any) {
      console.error("Error updating review:", err);
      alert(t("common.error", { defaultValue: "Failed to update review" }));
      throw err;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <ReviewsHeader onRefresh={fetchReviews} onAddReview={() => setCreateDialogOpen(true)} />

      {error && <ReviewsErrorState error={error} />}

      {loading ? (
        <ReviewsLoadingState />
      ) : reviews.length === 0 ? (
        <ReviewsEmptyState />
      ) : (
        <>
          <ReviewsGrid reviews={reviews} onDelete={handleDelete} onEdit={handleEdit} />
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <ReviewEditDialog
        review={editingReview}
        open={Boolean(editingReview)}
        onClose={() => setEditingReview(null)}
        onSave={handleEdit}
      />

      <ReviewCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={fetchReviews}
      />
    </Container>
  );
};

export default SellerReviews;